import { AxiosError } from "axios";
import { toast } from "sonner";

import type { ApiResponse, ResponseMeta } from "@/types/api";
import type { Buyer, BuyerRequest, BuyerRequestBatch, VendorRequest } from "@/types/product-request";

import { useMutation, useQuery } from "@tanstack/react-query";

import { usePrivateAxios } from "./use-private-axios";

// Create a new buyer request
export function useCreateProductRequest() {
	const privateAxios = usePrivateAxios();
	return useMutation({
		onError: (error) => {
			let message = "An error occurred";
			if (error instanceof AxiosError)
				message = error.response?.data?.message || error.message;
			else
				message = error.message;
			toast.error(message);
		},
		mutationFn: async (data: any) => {
			const response = await privateAxios.post("/buyer-requests", data);
			return response.data;
		}
	});
}

// Get all buyer requests (with pagination and meta)
export interface BuyerRequestsResponse extends ApiResponse {
	data: BuyerRequest[];

}

export function useGetMyRequests(params?: { page?: number; limit?: number }) {
	const privateAxios = usePrivateAxios();
	return useQuery({
		queryKey: ["buyer-requests", params],
		queryFn: async () => {
			const query: Record<string, any> = {};
			if (params?.page)
				query.page = params.page;
			if (params?.limit)
				query.limit = params.limit;
			const response = await privateAxios.get<BuyerRequestsResponse>(
				"/buyer-requests/my-requests",
				{ params: query }
			);
			return response.data;
		}
	});
}

export interface ProductRequestResponse {
	data: {
		data: (BuyerRequest & { buyer: Buyer })[];
		meta: ResponseMeta;
	};
	message: string;
	isSuccessful: boolean;
}
interface VendorRequestResponse {
	data: {
		data: (Omit<VendorRequest, "vendor"> & { batch: BuyerRequestBatch })[];
		meta: ResponseMeta;
	};
	message: string;
	isSuccessful: boolean;
}

// Get active buyer requests
export function useGetMyActiveRequests() {
	const privateAxios = usePrivateAxios();
	return useQuery({
		queryKey: ["buyer-requests", "active"],
		queryFn: async () => {
			const response = await privateAxios.get<ProductRequestResponse>("/buyer-requests/my-active-requests");
			return response.data;
		}
	});
}

// Get available requests for vendor
export function useGetAvailableRequests() {
	const privateAxios = usePrivateAxios();
	return useQuery({
		queryKey: ["buyer-requests", "vendor", "available"],
		queryFn: async () => {
			const response = await privateAxios.get<VendorRequestResponse>("/buyer-requests/available");
			return response.data;
		}
	});
}

// Get buyer request by ID
export function useGetRequestById(id: string) {
	const privateAxios = usePrivateAxios();
	return useQuery({
		queryKey: ["buyer-requests", id],
		queryFn: async () => {
			const response = await privateAxios.get(`/buyer-requests/${id}`);
			return response.data;
		},
		enabled: !!id
	});
}

// Respond to a buyer request
export function useRespondToRequest() {
	const privateAxios = usePrivateAxios();
	return useMutation({
		mutationFn: async ({ requestId, accept }: { requestId: string; accept: boolean }) => {
			const response = await privateAxios.post<VendorRequest>(
				`/buyer-requests/${requestId}/respond`,
				{ accept }
			);
			return response.data;
		},
		onSuccess: () => toast.success("Request responded successfully"),
		onError: (error) => {
			let message = "An error occurred";
			if (error instanceof AxiosError)
				message = error.response?.data?.message || error.message;
			else
				message = error.message;
			toast.error(message);
		}
	});
}

// Link contract to vendor response
export function useLinkContractToResponse() {
	const privateAxios = usePrivateAxios();
	return useMutation({
		mutationFn: async ({
			responseId,
			contractId
		}: {
			responseId: string;
			contractId: string;
		}) => {
			const response = await privateAxios.post(
				`/buyer-requests/vendor-response/${responseId}/link-contract/${contractId}`
			);
			return response.data;
		}
	});
}

// Get vendors who have accepted a specific request
export function useGetAcceptedVendors(requestId: string) {
	const privateAxios = usePrivateAxios();
	return useQuery({
		queryKey: ["buyer-requests", requestId, "accepted-vendors"],
		queryFn: async () => {
			const response = await privateAxios.get(`/buyer-requests/${requestId}/accepted-vendors`);
			return response.data;
		},
		enabled: !!requestId
	});
}

// Get vendor responses for a request
export function useVendorResponses(requestId: string) {
	const privateAxios = usePrivateAxios();
	return useQuery({
		queryKey: ["buyer-requests", requestId, "responses"],
		queryFn: async () => {
			const response = await privateAxios.get<VendorRequest[]>(`/buyer-requests/${requestId}/responses`);
			return response.data;
		},
		enabled: !!requestId
	});
}

// Get vendors available for a request
// export function useVendorsForRequest(requestId: string | null) {
// 	const privateAxios = usePrivateAxios();
// 	return useQuery({
// 		queryKey: ["buyer-requests", requestId, "vendors"],
// 		queryFn: async () => {
// 			const response = await privateAxios.get<Vendor[]>(`/buyer-requests/${requestId}/vendors`);
// 			return response.data;
// 		},
// 		enabled: !!requestId
// 	});
// }
